import ModalSearchLayout from "@/components/layout/ModalSearchLayout";
import { Button } from "@/components/ui/Button";
import { InputBox } from "@/components/ui/InputBox";
import SelectBox from "@/components/ui/SelectBox";
import Table, { TableColumn } from "@/components/ui/Table";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useCodeListByPriceCode, useCodeList } from "@/hooks/standard/st_code/useCode";
import { usePriceCode } from "@/hooks/standard/st_price_code/usePriceCode";
import CountBox from "@/components/ui/CountBox";
import { formatNumber } from "@/lib/utils";

interface PriceCodeItem {
  codeId: number;
  name: string;
  code?: string;
  sortOrder?: number;
}

interface PriceUnitItem {
  stPriceUnitCode: number;
  stName: string;
  stDist: string;
  [key: string]: any; // 동적 중량 컬럼
}

interface SelectedPriceInfo {
  priceCodeId: number;
  companyName: string;
  region: string;
  dist: string;
  weight: number;
  unitPrice: number;
}

interface PriceCodeSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (priceInfo: SelectedPriceInfo) => void;
}

export default function PriceCodeSearchModal({ isOpen, onClose, onSelect }: PriceCodeSearchModalProps) {
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedCompany, setSelectedCompany] = useState<PriceCodeItem | null>(null);
  const [selectedArrival, setSelectedArrival] = useState<number | undefined>(undefined); // 도착지 선택
  const [selectedRegionRow, setSelectedRegionRow] = useState<number[]>([]);
  const [selectedWeight, setSelectedWeight] = useState<string>(""); // 선택된 중량

  // 1. 단가코드 회사 목록 조회
  const { data: priceCodeList = [], isLoading: isLoadingCompanies } = useCodeListByPriceCode();

  // 2. 선택한 회사의 하위 도착지 목록 조회
  const { data: arrivalList = [] } = useCodeList(
    { parentId: selectedCompany?.codeId },
    { enabled: !!selectedCompany?.codeId }
  );

  // 3. 선택한 도착지의 단가 조회
  const { data: priceUnitData, isLoading: isLoadingPrices } = usePriceCode(
    { stPriceUnitCode: selectedArrival?.toString() },
    { enabled: !!selectedArrival }
  );

  // 도착지 목록이 로드되면 첫 번째 항목을 자동 선택
  useEffect(() => {
    if (arrivalList && arrivalList.length > 0 && !selectedArrival) {
      setSelectedArrival(arrivalList[0].codeId);
    }
  }, [arrivalList, selectedArrival]);

  // 단가 데이터 content 추출 (API 응답 구조에 따라 다를 수 있음)
  const priceContent = useMemo(() => {
    if (!priceUnitData) return [];
    const rawData = priceUnitData as any;
    return rawData?.content || rawData?.data?.content || [];
  }, [priceUnitData]);

  // 검색어로 필터링된 회사 목록
  const filteredCompanies = useMemo(() => {
    if (!searchKeyword.trim()) return priceCodeList;
    return priceCodeList.filter((item: PriceCodeItem) =>
      item.name?.toLowerCase().includes(searchKeyword.toLowerCase())
    );
  }, [priceCodeList, searchKeyword]);

  // 도착지 SelectBox 옵션
  const arrivalOptions = useMemo(() => {
    return arrivalList.map((item: any) => ({
      value: item.codeId?.toString() || "",
      label: item.name || "",
    }));
  }, [arrivalList]);

  // 단가 데이터에서 중량 컬럼 추출
  const weightColumns = useMemo(() => {
    if (!priceContent || priceContent.length === 0) return [];
    const firstRow = priceContent[0];
    return Object.keys(firstRow)
      .filter(key => !["stName", "stDist", "stPriceUnitCode"].includes(key))
      .sort((a, b) => parseFloat(a) - parseFloat(b));
  }, [priceContent]);

  // 중량 선택 옵션
  const weightOptions = useMemo(() => {
    return weightColumns.map(weight => ({
      value: weight,
      label: `${weight}t`,
    }));
  }, [weightColumns]);

  // 첫 번째 중량을 기본값으로 설정
  useEffect(() => {
    if (weightColumns.length > 0 && !selectedWeight) {
      setSelectedWeight(weightColumns[0]);
    }
  }, [weightColumns, selectedWeight]);

  // 회사 목록 컬럼
  const companyColumns: TableColumn[] = [
    { key: "name", header: "단가코드명", bodyAlign: "left" },
  ];

  // 지역별 단가 컬럼 (동적)
  const priceColumns: TableColumn[] = useMemo(() => {
    const baseColumns: TableColumn[] = [
      { key: "stName", header: "지역", bodyAlign: "left" },
      { key: "stDist", header: "거리" },
    ];

    weightColumns.forEach(weight => {
      baseColumns.push({
        key: weight,
        header: `${weight}t`,
        render: (value: any) => value ? formatNumber(value) : "-",
      });
    });

    return baseColumns;
  }, [weightColumns]);

  // 회사 선택 핸들러 (더블클릭)
  const handleCompanySelect = useCallback((item: PriceCodeItem) => {
    setSelectedCompany(item);
    setSelectedArrival(undefined); // 도착지 선택 초기화
    setSelectedRegionRow([]);
    setSelectedWeight("");
  }, []);

  // 지역 행 더블클릭 핸들러
  const handleRegionDoubleClick = useCallback((item: PriceUnitItem) => {
    if (!selectedCompany) return;

    const weight = selectedWeight || weightColumns[0];
    if (!weight) return;

    const unitPrice = item[weight];

    if (unitPrice) {
      onSelect({
        priceCodeId: selectedCompany.codeId,
        companyName: selectedCompany.name,
        region: item.stName,
        dist: item.stDist,
        weight: parseFloat(weight),
        unitPrice: Number(unitPrice),
      });
      onClose();
    }
  }, [selectedWeight, weightColumns, selectedCompany, onSelect, onClose]);

  // 확인 버튼 핸들러
  const handleConfirm = useCallback(() => {
    if (!selectedCompany || selectedRegionRow.length === 0) return;
    if (!priceContent || priceContent.length === 0) return;

    const row = priceContent[selectedRegionRow[0]];
    const weight = selectedWeight || weightColumns[0];
    if (!weight) return;

    const unitPrice = row[weight];

    if (row && unitPrice) {
      onSelect({
        priceCodeId: selectedCompany.codeId,
        companyName: selectedCompany.name,
        region: row.stName,
        dist: row.stDist,
        weight: parseFloat(weight),
        unitPrice: Number(unitPrice),
      });
      onClose();
    }
  }, [selectedCompany, selectedRegionRow, selectedWeight, weightColumns, priceContent, onSelect, onClose]);

  // 선택된 단가 미리보기
  const selectedPrice = useMemo(() => {
    if (!priceContent || priceContent.length === 0 || selectedRegionRow.length === 0) return null;
    const row = priceContent[selectedRegionRow[0]];
    const weight = selectedWeight || weightColumns[0];
    if (!row || !weight) return null;
    return row[weight];
  }, [priceContent, selectedRegionRow, selectedWeight, weightColumns]);

  // 모달이 닫힐 때 상태 초기화
  useEffect(() => {
    if (!isOpen) {
      setSearchKeyword("");
      setSelectedCompany(null);
      setSelectedArrival(undefined);
      setSelectedRegionRow([]);
      setSelectedWeight("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <ModalSearchLayout isOpen={isOpen} onClose={onClose} title="단가코드 검색" width="w-[1200px]">
      <div className="h-full flex flex-col">
        {/* 검색 조건 */}
        <div className="mb-3 flex-shrink-0">
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <InputBox
                label="단가코드명"
                value={searchKeyword}
                onChange={e => setSearchKeyword(e.target.value)}
                placeholder="단가코드명을 입력하세요"
              />
            </div>
          </div>
        </div>

        {/* 2단 레이아웃 */}
        <div className="flex gap-4 flex-1 min-h-0">
          {/* 왼쪽: 회사(단가코드) 목록 */}
          <div className="w-[250px] flex-shrink-0 flex flex-col">
            <div className="text-sm font-medium mb-2 text-gray-700">단가코드 목록</div>
            <div className="flex-1 min-h-0 border rounded">
              <Table
                columns={companyColumns}
                data={filteredCompanies}
                onRowDoubleClick={handleCompanySelect}
                highlightOnClick={true}
                maxHeight="calc(100vh - 400px)"
                loading={isLoadingCompanies}
                emptyMessage="단가코드가 없습니다."
              />
            </div>
            <div className="mt-2">
              <CountBox label="조회" count={filteredCompanies.length} />
            </div>
          </div>

          {/* 오른쪽: 도착지 선택 + 지역별 단가 */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* 도착지 + 중량 선택 */}
            <div className="flex gap-3 mb-2">
              <div className="flex-1">
                <SelectBox
                  label="도착지"
                  value={selectedArrival?.toString() || ""}
                  onChange={(value) => {
                    setSelectedArrival(Number(value));
                    setSelectedRegionRow([]);
                    setSelectedWeight("");
                  }}
                  options={arrivalOptions}
                  placeholder={selectedCompany ? "도착지 선택" : "단가코드를 먼저 선택하세요"}
                  disabled={!selectedCompany}
                />
              </div>
              <div className="w-[150px]">
                <SelectBox
                  label="중량"
                  value={selectedWeight}
                  onChange={setSelectedWeight}
                  options={weightOptions}
                  placeholder="중량"
                  disabled={weightOptions.length === 0}
                />
              </div>
            </div>

            {/* 지역별 단가 테이블 */}
            <div className="text-sm font-medium mb-2 text-gray-700">
              {selectedCompany && selectedArrival
                ? `${selectedCompany.name} - 지역별 단가`
                : "단가코드와 도착지를 선택하세요"}
            </div>
            <div className="flex-1 min-h-0 border rounded">
              <Table
                columns={priceColumns}
                data={priceContent}
                onRowDoubleClick={handleRegionDoubleClick}
                onRowSelect={setSelectedRegionRow}
                highlightOnClick={true}
                maxHeight="calc(100vh - 450px)"
                loading={isLoadingPrices}
                emptyMessage={
                  !selectedCompany
                    ? "왼쪽에서 단가코드를 더블클릭하세요."
                    : !selectedArrival
                      ? "도착지를 선택하세요."
                      : "단가 정보가 없습니다."
                }
              />
            </div>
            {selectedRegionRow.length > 0 && priceContent.length > 0 && selectedPrice && (
              <div className="mt-2 text-sm text-blue-600 font-medium">
                선택: {priceContent[selectedRegionRow[0]]?.stName} / {selectedWeight || weightColumns[0]}t / {formatNumber(selectedPrice)}원
              </div>
            )}
          </div>
        </div>

        {/* 하단 컨트롤 */}
        <div className="flex items-center justify-end mt-4 pt-4 border-t border-gray-300 flex-shrink-0">
          <div className="flex gap-2">
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={!selectedCompany || selectedRegionRow.length === 0 || !selectedPrice}
            >
              확인
            </Button>
            <Button variant="white" onClick={onClose}>
              닫기
            </Button>
          </div>
        </div>
      </div>
    </ModalSearchLayout>
  );
}
